import {format, isBefore, isAfter, isSameDay, startOfDay, endOfDay, addDays, subDays} from "date-fns";
import {ja} from "date-fns/locale";

// 共通フォーマット定数
export const DATE_FORMAT = "yyyy.MM.dd";
export const DATETIME_FORMAT = "yyyy.MM.dd HH:mm";
export const DATETIME_WEEKDAY_FORMAT = "yyyy.MM.dd(E) HH:mm";
export const DATE_WEEKDAY_FORMAT = "yyyy.MM.dd(E)";

/**
 * 文字列/DateをDateオブジェクトに変換
 */
export function toDate(date: Date | string | null | undefined): Date | null {
  if (!date) return null;
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return null;
  return d;
}

/**
 * 現在日時を取得
 */
export function getNow(): Date {
  return new Date();
}

/**
 * 日付を統一フォーマット (yyyy.MM.dd) で表示
 * @example formatDate(new Date()) // "2026.01.11"
 */
export function formatDate(date: Date | string | null | undefined): string {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "-";
  return format(d, DATE_FORMAT, {locale: ja});
}

/**
 * 日時を統一フォーマット (yyyy.MM.dd HH:mm) で表示
 * @example formatDateTime(new Date()) // "2026.01.11 19:30"
 */
export function formatDateTime(date: Date | string | null | undefined): string {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "-";
  return format(d, DATETIME_FORMAT, {locale: ja});
}

/**
 * 日時を曜日付きフォーマット (yyyy.MM.dd(E) HH:mm) で表示
 * @example formatDateTimeWithWeekday(new Date()) // "2026.01.11(土) 19:30"
 */
export function formatDateTimeWithWeekday(date: Date | string | null | undefined): string {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "-";
  return format(d, DATETIME_WEEKDAY_FORMAT, {locale: ja});
}

/**
 * 日付を曜日付きフォーマット (yyyy.MM.dd(E)) で表示
 * @example formatDateWithWeekday(new Date()) // "2026.01.11(土)"
 */
export function formatDateWithWeekday(date: Date | string | null | undefined): string {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "-";
  return format(d, DATE_WEEKDAY_FORMAT, {locale: ja});
}

/**
 * 日付範囲を統一フォーマットで表示
 * @example formatDateRange(from, to) // "2026.01.01 - 2026.01.31"
 */
export function formatDateRange(from: Date | null | undefined, to: Date | null | undefined): string {
  if (!from) return "";
  if (!to) return formatDate(from);
  return `${formatDate(from)} - ${formatDate(to)}`;
}

/**
 * 日時の範囲を表示する。同日なら終了側の日付を省略して時刻だけ、日跨ぎなら両端に日付を出す。
 * 終了が未確定（null）の場合は開始日時のみ返す（取込セッションの進行中表示など）。
 * @example formatDateTimeRange("2026-01-22T14:30+09", "2026-01-22T15:45+09") // "2026.01.22 14:30〜15:45"
 * @example formatDateTimeRange("2026-01-22T23:30+09", "2026-01-23T00:15+09") // "2026.01.22 23:30〜2026.01.23 00:15"
 * @example formatDateTimeRange("2026-01-22T14:30+09", null) // "2026.01.22 14:30"
 */
export function formatDateTimeRange(
  from: Date | string | null | undefined,
  to: Date | string | null | undefined,
): string {
  const fromDate = toDate(from);
  if (!fromDate) return "-";
  const toDateValue = toDate(to);
  if (!toDateValue) return formatDateTime(fromDate);
  return isSameDay(fromDate, toDateValue)
    ? `${formatDateTime(fromDate)}〜${formatTimeOnly(toDateValue)}`
    : `${formatDateTime(fromDate)}〜${formatDateTime(toDateValue)}`;
}

/**
 * 時刻のみを表示 (HH:mm)
 * @example formatTimeOnly("2026-01-22T14:30:00") // "14:30"
 */
export function formatTimeOnly(date: Date | string | null | undefined): string {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "-";
  return format(d, "HH:mm", {locale: ja});
}

/**
 * 曜日のみを表示 (E)
 * @example formatWeekday("2026-01-22") // "水"
 */
export function formatWeekday(date: Date | string | null | undefined): string {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "-";
  return format(d, "E", {locale: ja});
}

/**
 * 日付を日本語フォーマット (yyyy年MM月dd日) で表示
 * @example formatDateJp("2026-01-22") // "2026年01月22日"
 */
export function formatDateJp(date: Date | string | null | undefined): string {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "-";
  return format(d, "yyyy年MM月dd日", {locale: ja});
}

/**
 * 日付を日本語曜日付きフォーマット (yyyy年MM月dd日(E)) で表示
 * @example formatDateJpWithWeekday("2026-01-22") // "2026年01月22日(水)"
 */
export function formatDateJpWithWeekday(date: Date | string | null | undefined): string {
  if (!date) return "-";
  const d = typeof date === "string" ? new Date(date) : date;
  if (isNaN(d.getTime())) return "-";
  return format(d, "yyyy年MM月dd日(E)", {locale: ja});
}

// date-fns のユーティリティ関数を再エクスポート
export {isBefore, isAfter, isSameDay, startOfDay, endOfDay, addDays, subDays};
